import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { Room, RoomType, RoomStatus, CreateRoomRequest } from '../types/rooms';
import { roomService } from '../services/roomService';

const STATUSES: RoomStatus[] = ['available', 'occupied', 'maintenance', 'out_of_service'];

interface FormState {
  room_type_id: string;
  room_number: string;
  floor: number;
  notes: string;
}

const emptyForm: FormState = { room_type_id: '', room_number: '', floor: 1, notes: '' };

export default function RoomsPage() {
  const { t } = useTranslation();
  const [rooms, setRooms] = useState<Room[]>([]);
  const [roomTypes, setRoomTypes] = useState<RoomType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterType, setFilterType] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [statusMenu, setStatusMenu] = useState<string | null>(null);
  const statusMenuRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!statusMenu) return;
    const handleClick = (e: MouseEvent) => {
      if (statusMenuRef.current && !statusMenuRef.current.contains(e.target as Node)) {
        setStatusMenu(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [statusMenu]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const filters: any = {};
      if (filterStatus) filters.status = filterStatus;
      if (filterType) filters.room_type_id = filterType;
      const [roomsData, typesData] = await Promise.all([
        roomService.listRooms(filters),
        roomService.listRoomTypes(),
      ]);
      setRooms(roomsData);
      setRoomTypes(typesData);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterType]);

  useEffect(() => { load(); }, [load]);

  const statusLabel = (s: RoomStatus): string => {
    const map: Record<RoomStatus, string> = {
      available: t('rooms.available'),
      occupied: t('rooms.occupied'),
      maintenance: t('rooms.maintenance'),
      out_of_service: t('rooms.outOfService'),
    };
    return map[s] || s;
  };

  const statusColor = (s: RoomStatus): string => {
    const map: Record<RoomStatus, string> = {
      available: 'bg-green-100 text-green-800',
      occupied: 'bg-red-100 text-red-800',
      maintenance: 'bg-yellow-100 text-yellow-800',
      out_of_service: 'bg-gray-100 text-gray-800',
    };
    return map[s] || 'bg-gray-100 text-gray-800';
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...emptyForm, room_type_id: roomTypes[0]?.id || '' });
    setFormError(null);
    setShowForm(true);
  };

  const openEdit = (room: Room) => {
    setEditingId(room.id);
    setForm({
      room_type_id: room.room_type_id,
      room_number: room.room_number,
      floor: room.floor,
      notes: room.notes || '',
    });
    setFormError(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.room_number.trim()) { setFormError(t('rooms.number') + ' required'); return; }
    if (!form.room_type_id) { setFormError(t('rooms.type') + ' required'); return; }

    const payload: CreateRoomRequest = {
      room_type_id: form.room_type_id,
      room_number: form.room_number.trim(),
      floor: form.floor,
      notes: form.notes.trim() || undefined,
    };

    try {
      setSaving(true);
      setFormError(null);
      if (editingId) {
        await roomService.updateRoom(editingId, payload);
      } else {
        await roomService.createRoom(payload);
      }
      setShowForm(false);
      await load();
    } catch (e: any) {
      setFormError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (roomId: string, newStatus: RoomStatus) => {
    try {
      await roomService.updateRoomStatus(roomId, { status: newStatus });
      setStatusMenu(null);
      await load();
    } catch (e: any) {
      setError(e.message);
      setStatusMenu(null);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await roomService.deleteRoom(id);
      setDeleteConfirm(null);
      await load();
    } catch (e: any) {
      setError(e.message);
      setDeleteConfirm(null);
    }
  };

  const counts = {
    total: rooms.length,
    available: rooms.filter(r => r.status === 'available').length,
    occupied: rooms.filter(r => r.status === 'occupied').length,
    maintenance: rooms.filter(r => r.status === 'maintenance' || r.status === 'out_of_service').length,
  };

  return (
    <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('rooms.title')}</h1>
          <p className="text-sm text-gray-500 mt-1">{t('rooms.subtitle')}</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/room-types" className="px-4 py-2 border border-purple-300 text-purple-700 rounded-md hover:bg-purple-50 transition text-sm">
            {t('nav.roomTypes')}
          </Link>
          <button onClick={openCreate} disabled={roomTypes.length === 0}
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition disabled:opacity-50">
            {t('rooms.create')}
          </button>
        </div>
      </div>

      {roomTypes.length === 0 && !loading && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded text-amber-800 text-sm">
          {t('rooms.needRoomTypes')}{' '}
          <Link to="/admin/room-types" className="underline font-medium">{t('nav.roomTypes')}</Link>
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={load} className="text-sm underline">{t('common.retry')}</button>
        </div>
      )}

      {/* Inventory summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: t('rooms.total'), value: counts.total, color: 'bg-slate-50 text-slate-800' },
          { label: t('rooms.available'), value: counts.available, color: 'bg-green-50 text-green-800' },
          { label: t('rooms.occupied'), value: counts.occupied, color: 'bg-red-50 text-red-800' },
          { label: t('rooms.maintenance'), value: counts.maintenance, color: 'bg-yellow-50 text-yellow-800' },
        ].map(card => (
          <div key={card.label} className={`rounded-lg border p-3 ${card.color}`}>
            <div className="text-xs font-medium uppercase opacity-80">{card.label}</div>
            <div className="text-2xl font-bold mt-1">{card.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex gap-4 mb-4">
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
          aria-label={t('rooms.filterByStatus')}>
          <option value="">{t('rooms.allStatuses')}</option>
          {STATUSES.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
        </select>
        <select value={filterType} onChange={e => setFilterType(e.target.value)}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-purple-500 focus:border-purple-500"
          aria-label={t('rooms.filterByType')}>
          <option value="">{t('rooms.allTypes')}</option>
          {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
        </select>
      </div>

      {/* Form */}
      {showForm && (
        <div className="mb-6 bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4">{editingId ? t('rooms.edit') : t('rooms.create')}</h2>
          {formError && <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-red-600 text-sm">{formError}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rooms.number')}</label>
                <input type="text" value={form.room_number} onChange={e => setForm(f => ({ ...f, room_number: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" maxLength={20} required
                  placeholder={t('rooms.numberPlaceholder')} />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rooms.floor')}</label>
                <input type="number" value={form.floor} onChange={e => setForm(f => ({ ...f, floor: parseInt(e.target.value) || 1 }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('rooms.type')}</label>
                <select value={form.room_type_id} onChange={e => setForm(f => ({ ...f, room_type_id: e.target.value }))}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" required>
                  <option value="">—</option>
                  {roomTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('rooms.notes')}</label>
              <input type="text" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:ring-purple-500 focus:border-purple-500" />
            </div>
            <div className="flex gap-3">
              <button type="submit" disabled={saving} className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50">
                {saving ? t('common.loading') : t('common.save')}
              </button>
              <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50">
                {t('common.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="text-center py-12 text-gray-500">{t('common.loading')}</div>
      ) : rooms.length === 0 ? (
        <div className="text-center py-12 text-gray-500">{t('rooms.noData')}</div>
      ) : (
        <div className="bg-white shadow rounded-lg overflow-visible">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.number')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.floor')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.status')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rooms.notes')}</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('rooms.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {rooms.map(room => (
                <tr key={room.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{room.room_number}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">{room.floor}</td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {room.room_type_name
                      || roomTypes.find(rt => rt.id === room.room_type_id)?.name
                      || '—'}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusColor(room.status)}`}>
                      {statusLabel(room.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">{room.notes || '—'}</td>
                  <td className="px-6 py-4 text-right text-sm space-x-2 relative">
                    <button onClick={() => openEdit(room)} className="text-purple-600 hover:text-purple-800">{t('rooms.edit')}</button>
                    {/* Status change dropdown */}
                    <span className="relative inline-block" ref={statusMenu === room.id ? statusMenuRef : undefined}>
                      <button onClick={() => setStatusMenu(statusMenu === room.id ? null : room.id)}
                        className="text-blue-600 hover:text-blue-800">{t('rooms.changeStatus')}</button>
                      {statusMenu === room.id && (
                        <div className="absolute right-0 bottom-full mb-1 w-40 bg-white border border-gray-200 rounded shadow-lg z-10">
                          {STATUSES.filter(s => s !== room.status).map(s => (
                            <button key={s} onClick={() => handleStatusChange(room.id, s)}
                              className="block w-full text-left px-3 py-2 text-sm hover:bg-gray-100">{statusLabel(s)}</button>
                          ))}
                        </div>
                      )}
                    </span>
                    {deleteConfirm === room.id ? (
                      <>
                        <button onClick={() => handleDelete(room.id)} className="text-red-600 hover:text-red-800 font-medium">{t('common.confirm')}</button>
                        <button onClick={() => setDeleteConfirm(null)} className="text-gray-500 hover:text-gray-700">{t('common.cancel')}</button>
                      </>
                    ) : (
                      <button onClick={() => setDeleteConfirm(room.id)} className="text-red-500 hover:text-red-700">{t('rooms.delete')}</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
